import type { FC, ReactNode } from 'react'
import type { Client } from '@/types'

import Image from 'next/image'
import { cn } from '@/lib/utils'

interface ClientsMarqueeProps {
    clients: Client[]
    title?: string
    className?: string
}

const LogoRow: FC<{ clients: Client[], hidden?: boolean }> = ({ clients, hidden = false }): ReactNode => {
    return (
        <ul aria-hidden={hidden ? 'true' : undefined} className='flex shrink-0 items-center gap-6 pr-6 sm:gap-10 sm:pr-10'>
            {clients.map((client) => (
                <li key={client.id} className='relative h-14 w-32 shrink-0 sm:h-16 sm:w-40'>
                    {client.logo && (
                        <Image
                            src={client.logo}
                            alt={hidden ? '' : client.logoAlt || client.name}
                            fill
                            sizes='160px'
                            className='object-contain opacity-75 grayscale transition duration-300 hover:opacity-100 hover:grayscale-0'
                        />
                    )}
                </li>
            ))}
        </ul>
    )
}

export const ClientsMarquee: FC<ClientsMarqueeProps> = ({ clients, title = 'Trusted by leading brands', className }): ReactNode => {
    const withLogos = clients.filter((client) => client.logo)

    if (withLogos.length === 0) return null

    return (
        <section id='trusted-by' aria-label={title} className={cn('border-y border-ink/8 bg-white py-12', className)}>
            <p className='px-6 text-center text-xs font-bold tracking-[0.24em] text-muted-foreground uppercase'>
                {title}
            </p>

            <div className='relative mt-8 overflow-hidden [mask-image:linear-gradient(90deg,transparent,black_10%,black_90%,transparent)]'>
                <div className='flex w-max motion-safe:animate-marquee hover:[animation-play-state:paused]'>
                    <LogoRow clients={withLogos} />
                    <LogoRow clients={withLogos} hidden />
                </div>
            </div>
        </section>
    )
}
